import React, { useState } from 'react';
import moment from 'moment';
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import DoneIcon from '@mui/icons-material/Done';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import { useStyles } from "./styles";

const URL_RE = /(https?:\/\/[^\s<]+|www\.[^\s<]+)/gi;
const PHONE_RE = /(\+?\d[\d\s().-]{6,}\d)/g;
// One to three emoji (with modifiers / ZWJ sequences) and nothing else.
const EMOJI_ONLY_RE = /^(?:\p{Extended_Pictographic}️?(?:‍\p{Extended_Pictographic}️?)*\s*){1,3}$/u;

// Turns URLs and phone numbers in a message into links.
const renderRichText = (text, linkClass) => {
  const nodes = [];
  let key = 0;
  const pushPhones = (chunk) => {
    let last = 0;
    chunk.replace(PHONE_RE, (match, _p, offset) => {
      if (offset > last) nodes.push(chunk.slice(last, offset));
      nodes.push(<a key={key++} className={linkClass} href={`tel:${match.replace(/[^\d+]/g, '')}`}>{match}</a>);
      last = offset + match.length;
      return match;
    });
    if (last < chunk.length) nodes.push(chunk.slice(last));
  };
  let last = 0;
  text.replace(URL_RE, (match, _p, offset) => {
    if (offset > last) pushPhones(text.slice(last, offset));
    const href = match.startsWith('http') ? match : `https://${match}`;
    nodes.push(<a key={key++} className={linkClass} href={href} target="_blank" rel="noopener noreferrer">{match}</a>);
    last = offset + match.length;
    return match;
  });
  if (last < text.length) pushPhones(text.slice(last));
  return nodes;
};

export default function Message(props) {
    const {
      data,
      isMine,
      startsSequence,
      dateSeparator,
      onRetry,
    } = props;
    const { classes, cx } = useStyles();
    const [mmsOpen, setMmsOpen] = useState(false);

    let mmsData = "";
    if (data.type === "mms" && data.file) {
      mmsData = data.file.startsWith("data:")
              ? data.file
              : `data:image/jpg;base64, ${data.file}`
    }

    const text = data.message || "";
    const failed = data.status === "failed";
    const delivered = data.status === "delivered" || data.status === "read";
    const emojiOnly = !mmsData && text.trim() !== "" && EMOJI_ONLY_RE.test(text.trim());
    const friendlyTimestamp = moment(data.dateTime).format('LLLL');
    const messageHour = moment(data.dateTime).format('LT');

    const statusIcon = !isMine ? null
      : failed ? <ErrorOutlineIcon titleAccess="Not delivered" />
      : delivered ? <DoneAllIcon titleAccess="Delivered" />
      : <DoneIcon titleAccess="Sent" />;

    return (
      <div className={classes.message}>
        {dateSeparator && (
          <div className={classes.dateSeparator}>
            <span>{dateSeparator}</span>
          </div>
        )}
        {mmsData && (
          <Lightbox
            open={mmsOpen}
            close={() => setMmsOpen(false)}
            slides={[{ src: mmsData }]}
            styles={{ root: { zIndex: 995 } }}
          />
        )}
        <div className={cx(classes.row, isMine && classes.rowMine, startsSequence && classes.rowStart)}>
          <div
            className={cx(
              classes.bubble,
              isMine ? classes.bubbleMine : classes.bubbleTheirs,
              startsSequence && (isMine ? classes.tailMine : classes.tailTheirs),
              failed && classes.bubbleFailed,
            )}
            title={friendlyTimestamp}
          >
            {mmsData && (
              <img className={classes.media} src={mmsData} alt="mms" onClick={() => setMmsOpen(true)} />
            )}
            {/* The time sits beside a short message and drops below a long one. */}
            <div className={classes.content}>
              {text !== "" && (
                <div className={cx(classes.text, emojiOnly && classes.emojiOnly)}>
                  {renderRichText(text, classes.link)}
                </div>
              )}
              <div className={cx(classes.meta, failed && classes.metaFailed)}>
                <span>{messageHour}</span>
                {statusIcon}
              </div>
            </div>
          </div>
        </div>
        {failed && (
          <div
            className={cx(classes.failedNote, isMine && classes.failedNoteMine, onRetry && classes.failedNoteRetry)}
            role={onRetry ? "button" : undefined}
            tabIndex={onRetry ? 0 : undefined}
            onClick={onRetry ? () => onRetry(data) : undefined}
            onKeyDown={onRetry ? (e) => { if (e.key === 'Enter') onRetry(data) } : undefined}
          >
            Not delivered{onRetry ? " · Tap to retry" : ""}
          </div>
        )}
      </div>
    );
}
